import {noop} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {
  generateCrossModelComparisonTables,
  getComparisonCellDisplayText,
  isCrossModelValidationVerified,
} from 'notebook/ai-tools/flexible-data-entry/CrossModelValidation';
import {formatTable} from 'notebook/ai-tools/flexible-data-entry/FormatComparisonTable';
import {FillStructuredTableUsingAIResponse} from 'notebook/ai-tools/NotebookAIToolsDAO';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {Table as TableConstants} from 'notebook/tables/TableConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import ToggleGroup from 'ui-library/ToggleGroup';
import {listToReadable} from 'util/NameUtil';

import {filterMismatchRows} from './FlexibleDataEntryFilterMismatchRows';
import {applyModelEdits, ModelEdits} from './FlexibleDataEntryResultEdits';

interface FlexibleDataEntryTablePreviewProps {
  structuredTable: StructuredTable<unknown, unknown>;
  fillResult: FillStructuredTableUsingAIResponse;
  selectedResultIndex: number;
  onSelectResultIndex: (index: number) => void;
  modelEdits: ModelEdits;
  onCellEdit: (modelIndex: number, rowIndex: number, columnIndex: number, newValue: string | null) => void;
}

export default function FlexibleDataEntryTablePreview({
  structuredTable,
  fillResult,
  selectedResultIndex,
  onSelectResultIndex,
  modelEdits,
  onCellEdit,
}: FlexibleDataEntryTablePreviewProps): JSX.Element | null {
  const [shouldHideMatchingRows, setShouldHideMatchingRows] = useState<boolean>(false);

  if (!assertAndContinue(fillResult.runResults.length > 0, 'Unexpected no run results.')) {
    return <div>No rows were generated.</div>;
  }

  const selectedResult = fillResult.runResults[selectedResultIndex];
  const comparisonTables = generateCrossModelComparisonTables(
    fillResult,
    selectedResultIndex,
    structuredTable.name
  );
  const editedTables = applyModelEdits(
    comparisonTables,
    modelEdits,
    selectedResultIndex,
    structuredTable.name
  );
  const {primaryTable, secondaryExtraTables} = filterMismatchRows(editedTables, shouldHideMatchingRows);

  const isVerified = isCrossModelValidationVerified(fillResult);
  const modelsDescription = listToReadable(fillResult.runResults.map((result) => result.displayName));

  const areAllModelsValid =
    fillResult.runResults.length > 1 && fillResult.runResults.every((result) => !result.hadError);

  const handleTableUpdate = (index: number, {table}: {table: TableConstants}): void => {
    for (let rowIndex = 0; rowIndex < table.table.length; rowIndex++) {
      for (let columnIndex = 0; columnIndex < table.table[rowIndex].length; columnIndex++) {
        const newCell = table.table[rowIndex][columnIndex];
        const oldCell = primaryTable[rowIndex][columnIndex];

        if (getComparisonCellDisplayText(oldCell) !== newCell.text) {
          const actualRowIndex = oldCell.originalRowIndex;
          const preEditCell = comparisonTables.primaryTable[actualRowIndex][columnIndex];

          // If cell matches pre-edit value, clear the edit. Otherwise update to new value
          if (newCell.text === getComparisonCellDisplayText(preEditCell)) {
            onCellEdit(selectedResultIndex, actualRowIndex, columnIndex, null);
          } else {
            onCellEdit(selectedResultIndex, actualRowIndex, columnIndex, newCell.text);
          }
        }
      }
    }
  };

  const renderPrimaryTable = (doesSecondaryExtraTablesExist: boolean): JSX.Element | null => {
    if (primaryTable.length > 0) {
      return (
        <SimpleTableEditable
          {...standaloneProps}
          locked={false}
          cid="tmp-flexible-data-entry-table-preview"
          table={{
            name: structuredTable.name,
            columnLabels: structuredTable.columns.map((c) => c.name),
            table: formatTable(primaryTable),
            widths: structuredTable.columns.map((c) => c.width),
            columnConstraints: structuredTable.columns.map((c) => null),
            allowAddRows: false,
            lockedDims: true,
          }}
          hideFormulaBox={true}
          enableFormulas={false}
          storeState={noop}
          restoreState={noop}
          updateHandler={handleTableUpdate}
        />
      );
    } else if (!doesSecondaryExtraTablesExist) {
      if (shouldHideMatchingRows) {
        return <div>No mismatched rows were generated.</div>;
      } else {
        return <div>No rows were generated.</div>;
      }
    }
    return null;
  };

  return (
    <div className="mediocre">
      {isVerified ? (
        <InlineBanner
          bannerType={InlineBannerType.SUCCESS}
          text={`Verification passed: AI models ${modelsDescription} produced the same result.`}
        />
      ) : (
        <div>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`Verification failed: AI models ${modelsDescription} produced different results, so the resulting data may contain inaccuracies. Carefully review the data below and click "Approve and insert rows" to approve the results from ${selectedResult.displayName}.`}
          />
          <div className={css(styles.suggestions)}>
            Suggestions to resolve discrepancies:
            <ul>
              <li>
                Retry the operation with additional AI instructions to clarify any ambiguities in the data
                entry task.
              </li>
              <li>
                If the AI system is performing a multi-step lookup process, directly add custom columns
                corresponding to intermediate steps.
              </li>
              <li>Retry the operation; results may vary slightly from run to run.</li>
            </ul>
          </div>
          {areAllModelsValid && (
            <>
              <FormGroup>
                <div className={css(styles.modelSelector)}>
                  <FieldLabel className={css(styles.modelSelector)} label="Select model result:" />
                  <ToggleGroup
                    options={fillResult.runResults.map((result, index) => ({
                      id: index,
                      buttonProps: {text: result.displayName},
                    }))}
                    selection={selectedResultIndex}
                    onChangeSelection={onSelectResultIndex}
                    size={ButtonSize.SMALL}
                    isFullWidth={false}
                  />
                </div>
              </FormGroup>
              <FormGroup>
                <CheckboxInput
                  id="hide-matching-rows"
                  checked={shouldHideMatchingRows}
                  onChange={(e) => {
                    setShouldHideMatchingRows(e.target.checked);
                  }}
                  label="Only show mismatch rows"
                />
              </FormGroup>
            </>
          )}
        </div>
      )}
      <div className={css(styles.primaryTableContainer)}>
        {renderPrimaryTable(secondaryExtraTables.length > 0)}
      </div>
      {secondaryExtraTables.map(({displayName, table}, i) => (
        <div key={displayName}>
          <div>Model {displayName} produced extra rows that will not be included:</div>
          <SimpleTableEditable
            {...standaloneProps}
            locked={true}
            cid={`tmp-flexible-data-entry-table-preview-secondary-${i}`}
            table={{
              name: `Extra rows from ${displayName}`,
              columnLabels: structuredTable.columns.map((c) => c.name),
              table: formatTable(table),
              widths: structuredTable.columns.map((c) => c.width),
              columnConstraints: structuredTable.columns.map((c) => null),
              allowAddRows: false,
              lockedDims: true,
            }}
            hideFormulaBox={true}
            enableFormulas={false}
            storeState={noop}
          />
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  suggestions: {
    marginTop: 8,
  },
  modelSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  modelSelectorLabel: {
    margin: 0,
  },
  primaryTableContainer: {
    marginTop: '8px',
    marginBottom: '8px',
  },
});
